import { Component, ComponentInterface, Element, h, Host, JSX, Listen, Prop } from '@stencil/core';
import { isKeyboardClickOnFocusedElement } from '../../utils/is-keyboard-click-on-focused-element';
import { SharedDirectionService } from '../../global/init-services';

export type CardVariant = 'primary' | 'secondary' | 'white' | 'light-grey' | 'dark-grey'; // Keeping these for compatibility

/**
 * Cards group functions or content to show structures and hierarchies. This page's card element only refers to the
 * general appearance. The card component accommodates varying title and description lengths and can be used in
 * accordions.
 *
 * @slot - Contents placed within the element
 */
@Component({
  tag: 'wb-card',
  styleUrl: './card.scss',
  shadow: true,
})
export class CardComponent implements ComponentInterface {
  @Element()
  public el: HTMLWbCardElement;

  /** @internal reflects global rtl */
  @Prop({ reflect: true, mutable: true })
  public isRtl: boolean = false;

  /** Color variant of the card */
  @Prop({ reflect: true })
  public variant: CardVariant = 'primary';

  /** Makes card appear interactive */
  @Prop({ reflect: true })
  public interactive: boolean = false;

  /** Makes card have recommended padding */
  @Prop({ reflect: true })
  public padded: boolean = false;

  public connectedCallback(): void {
    this.updateDirection();
  }

  @Listen('wbdirchange', { target: 'document' })
  public updateDirection() {
    this.isRtl = SharedDirectionService.isRtl;
  }

  @Listen('keydown')
  public handleKeyDown(ev: KeyboardEvent): void {
    if (this.interactive && isKeyboardClickOnFocusedElement(this.el, ev)) {
      ev.preventDefault();
      this.el.click();
    }
  }

  /**
   * This function sets the `data-on` attribute on the given element based on the current value and a given (deprecated)
   * variant. It exists for retro-compatibility reasons and should be removed on the next major version.
   *
   * DEFER TODO: Remove this function on the next major version.
   */
  private getDataOnFromVariant(): string | undefined {
    if (this.el.hasAttribute('data-on')) {
      return this.el.getAttribute('data-on');
    }
    const bodyDataBrand = document.body.getAttribute('data-brand');
    if (this.variant === 'white' || this.variant === 'light-grey') {
      return bodyDataBrand === 'amg' ? 'contrast' : 'main';
    }
    if (this.variant === 'dark-grey') {
      return bodyDataBrand === 'amg' ? 'main' : 'contrast';
    }
    return undefined;
  }

  public render(): JSX.Element {
    return (
      <Host class="wb-card" data-on={this.getDataOnFromVariant()} tabindex={this.interactive ? 0 : null} role={this.interactive ? 'button' : null}>
        <slot />
      </Host>
    );
  }
}
